import React, { useContext, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearchPlus,
  faSearchMinus,
  faAngleDoubleUp,
  faAngleDoubleDown,
} from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import { generateEloColorPalette } from "./utils/colorUtils";
import {
  grayColor,
  whiteColor,
} from "../../../../../assets/jss/material-dashboard-pro-react";

const scenarioTableStyle = (theme, clientColor, fontSize = 11) => {
  // Generate dynamic Elo colors based on client color
  const eloColors = generateEloColorPalette(clientColor, 5); // Generate 5 colors for potential Elos

  // Create dynamic Elo classes with dynamic font size
  const eloClasses = {};
  eloColors.forEach((color, index) => {
    eloClasses[`eloHeader${index}`] = {
      backgroundColor: color,
      color: `${whiteColor} !important`, // Force white text for visibility
      fontWeight: "bold",
      fontSize: `${fontSize}px`, // Same as base font size
      padding: "2px 1px",
      border: "none",
      textAlign: "center",
      maxWidth: "50px",
      minWidth: "40px",
      overflow: "visible", // Allow content to be visible
      whiteSpace: "normal", // Allow text wrapping
      lineHeight: "1.1", // Tighter line height for wrapped text
      verticalAlign: "top", // Align to top for multi-line headers
      position: "sticky",
      top: "24px", // Position exactly below scenario header
      zIndex: 3,
      minHeight: "20px", // Allow height to expand for wrapped text
      boxSizing: "border-box",
    };
  });

  return {
    scenarioColumn: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${grayColor[8]}`,
      borderRadius: "4px",
      backgroundColor: whiteColor,
      overflow: "hidden", // Changed from auto to hidden for outer container
      minWidth: "600px", // Increased minimum width to accommodate more columns
      height: "100%", // Ensure it takes full height of parent
    },
    scenarioContent: {
      flex: 1,
      overflow: "auto", // Enable scrolling for the table content
      fontSize: "10px",
      height: "100%", // Take full height of parent
      position: "relative", // For sticky positioning
      // Custom scrollbar styling to match the theme
      "&::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: grayColor[13],
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: grayColor[8],
        borderRadius: "4px",
        "&:hover": {
          backgroundColor: grayColor[6],
        },
      },
    },
    scenarioHeader: {
      backgroundColor: clientColor,
      color: whiteColor,
      fontSize: `${fontSize}px`,
      fontWeight: "bold",
      padding: "2px 4px", // Reduced padding for fixed height
      textAlign: "center",
      border: "none",
      position: "sticky",
      top: 0,
      zIndex: 4,
      height: "24px", // Fixed height
      minHeight: "24px",
      maxHeight: "24px", // Prevent expansion
      lineHeight: "20px", // Center text vertically
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap", // Prevent wrapping
      boxSizing: "border-box",
    },
    // PnL table styling (very compact)
    pnlTable: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: `${fontSize}px`,
    },
    nodeHeaderRow: {
      backgroundColor: grayColor[10],
      // Removed color override - let individual Elo header classes control color
      position: "sticky",
      top: "24px", // Position exactly below scenario header (24px) - no gap
      zIndex: 3,
      height: "24px", // Middle height for 70px total
      minHeight: "24px",
    },
    nodeHeaderCell: {
      // Base styles that will be combined with dynamic Elo classes
      // Background color and text color are set by Elo classes
      fontWeight: "bold",
      fontSize: `${fontSize}px`,
      padding: "2px 1px",
      border: "none",
      textAlign: "center",
      maxWidth: "50px",
      minWidth: "40px",
      overflow: "visible", // Allow content to be visible
      whiteSpace: "normal", // Allow text wrapping
      lineHeight: "1.1", // Tighter line height for wrapped text
      verticalAlign: "top", // Align to top for multi-line headers
      position: "sticky",
      top: "24px", // Position exactly below scenario header - no gap
      zIndex: 3,
      minHeight: "20px", // Allow height to expand for wrapped text
      boxSizing: "border-box",
    },
    headerRow: {
      backgroundColor: grayColor[12],
      position: "sticky",
      top: "48px", // Position exactly below scenario header (24px) + node header (24px) = 48px
      zIndex: 2,
      height: "20px", // Thinnest height for bottom row
      minHeight: "20px",
    },
    headerCell: {
      padding: "4px 2px",
      border: `1px solid ${grayColor[8]}`,
      fontSize: `${fontSize}px`,
      fontWeight: "bold",
      textAlign: "center",
      maxWidth: "50px",
      minWidth: "40px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
      lineHeight: "1.1",
      verticalAlign: "top",
      position: "sticky",
      top: "48px", // Position exactly below scenario header (24px) + node header (24px) = 48px
      zIndex: 2,
      height: "20px", // Thinnest height for bottom row
      minHeight: "20px",
      boxSizing: "border-box",
    },
    hierarchyHeaderCell: {
      position: "sticky",
      left: 0,
      top: 0,
      backgroundColor: grayColor[12],
      zIndex: 5, // Highest z-index for corner cell
      textAlign: "left",
      border: "none",
      borderRight: `2px solid ${grayColor[9]}`, // Add divider to separate dimensions from scenarios
      padding: "8px 4px 2px 4px",
      // Ensure it spans the full height and stays sticky
      height: "68px", // Total height: scenario (24px) + node (24px) + column (20px) = 68px
      minHeight: "68px",
      maxHeight: "68px",
    },
    expandCollapseContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      height: "100%",
      width: "100%",
      justifyContent: "center",
      padding: "4px",
    },
    expandCollapseButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px 6px",
      fontSize: "12px",
      cursor: "pointer",
      backgroundColor: whiteColor,
      border: `1px solid ${grayColor[8]}`,
      borderRadius: "4px",
      color: grayColor[2],
      transition: "all 0.2s ease",
      minHeight: "28px",
      "&:hover": {
        backgroundColor: grayColor[13],
        color: grayColor[1],
        borderColor: grayColor[6],
      },
      "&:active": {
        backgroundColor: grayColor[12],
      },
    },
    zoomButtonsContainer: {
      display: "flex",
      gap: "2px",
      width: "100%",
    },
    zoomButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px",
      fontSize: "10px",
      cursor: "pointer",
      backgroundColor: whiteColor,
      border: `1px solid ${grayColor[8]}`,
      borderRadius: "3px",
      color: grayColor[2],
      transition: "all 0.2s ease",
      flex: 1,
      minHeight: "24px",
      "&:hover": {
        backgroundColor: grayColor[13],
        color: grayColor[1],
        borderColor: grayColor[6],
      },
      "&:active": {
        backgroundColor: grayColor[12],
      },
      "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
        "&:hover": {
          backgroundColor: whiteColor,
          color: grayColor[2],
          borderColor: grayColor[8],
        },
      },
    },
    dataRow: {
      "&:hover": {
        backgroundColor: grayColor[13],
      },
    },
    hierarchyCell: {
      position: "sticky",
      left: 0,
      backgroundColor: whiteColor,
      zIndex: 2,
      padding: "4px 6px",
      border: `1px solid ${grayColor[8]}`,
      borderLeft: "none",
      borderRight: `2px solid ${grayColor[9]}`, // Add divider to separate dimensions from scenarios
      fontSize: `${fontSize}px`,
      minWidth: "80px",
      maxWidth: "120px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    valueCell: {
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
      fontSize: `${fontSize}px`,
      padding: "2px 1px",
      border: `1px solid ${grayColor[8]}`,
      borderLeft: "none",
      maxWidth: "50px",
      minWidth: "40px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    expandIcon: {
      fontSize: `${fontSize}px`,
      marginRight: "4px",
      color: grayColor[6],
      cursor: "pointer",
    },
    // PnL level styling
    // Removed bold styling for all rows
    placeholderText: {
      color: grayColor[8],
      fontStyle: "italic",
    },
    // Resize handle styling
    resizeHandle: {
      position: "absolute",
      top: 0,
      right: "-2px", // Position it exactly on the border
      width: "8px", // Wider for easier interaction
      height: "100%",
      cursor: "col-resize",
      backgroundColor: "transparent",
      zIndex: 6, // Higher than header cell
    },
    resizeHandleActive: {
      // Keep minimal styling even when active
      cursor: "col-resize",
    },
    // Spread dynamic Elo classes
    ...eloClasses,
  };
};

const ScenarioTable = ({
  selectedScenarios,
  hierarchyOrder, // eslint-disable-line no-unused-vars -- Reserved for future use
  visibleNodes,
  visibleColumns, // eslint-disable-line no-unused-vars -- Reserved for future use with column filtering
  columnOrganization,
  nodeStructure,
  metadata, // New prop for metadata
  getScenarioData,
  shouldItemBeVisible,
  handleDrillDownClick,
  drillDownsOpen,
  handleExpandAll,
  handleCollapseAll,
  formatNumber,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  // State for zoom functionality - must be declared before useStyles
  const [fontSize, setFontSize] = useState(11); // Default 11pt as requested

  const useStyles = makeStyles((theme) =>
    scenarioTableStyle(theme, clientColor, fontSize),
  );
  const classes = useStyles();

  // Helper function to determine if a column should display as percentage
  const isPercentageColumn = useCallback((columnKey, metadata) => {
    if (!metadata || !metadata.nodes) return false;

    // Parse column key to get node and metric info
    const parts = columnKey.split("_");
    if (parts.length < 3) return false;

    const nodeKey = parts[1];
    const metricId = parts.slice(2).join("_");

    // Check if this is a delta rate column (always percentage)
    if (parts[0] === "delta" && columnKey.includes("rate")) {
      return true;
    }

    // Check metadata for this specific column
    const nodeColumns = metadata.nodes[nodeKey]?.columns || [];
    const column = nodeColumns.find((col) => col.column_id === metricId);

    if (column) {
      // Check if column name contains "rate" or "%" or if calculation_type is delta_rate
      return (
        column.calculation_type === "delta_rate" ||
        column.column_name.toLowerCase().includes("rate") ||
        column.column_name.includes("%") ||
        columnKey.includes("rate")
      );
    }

    return false;
  }, []);

  // Helper function to format numbers with percentage symbol when appropriate
  const formatCellValue = useCallback(
    (value, columnKey, metadata) => {
      if (value === null || value === undefined) return "-";

      // For percentage columns, multiply by 100 and add % symbol
      if (isPercentageColumn(columnKey, metadata)) {
        const percentageValue = value * 100; // Convert 0.38 to 38
        const formattedPercentage = formatNumber(percentageValue);
        return `${formattedPercentage}%`;
      }

      // For non-percentage columns, use regular formatting
      return formatNumber(value);
    },
    [formatNumber, isPercentageColumn],
  );

  // State for resizable hierarchy column
  const [hierarchyColumnWidth, setHierarchyColumnWidth] = useState(120);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // Generate data for both scenarios - handle null scenarios gracefully
  const scenarioDataLeft =
    selectedScenarios[0] && selectedScenarios[0] !== null
      ? getScenarioData(0)
      : [];
  const scenarioDataRight =
    selectedScenarios[1] && selectedScenarios[1] !== null
      ? getScenarioData(1)
      : [];

  console.log("ScenarioTable data generation:", {
    scenarioDataLeftLength: scenarioDataLeft.length,
    scenarioDataRightLength: scenarioDataRight.length,
    selectedScenarios,
  });

  // Filter data for visibility
  const filteredDataLeft = scenarioDataLeft.filter((item) =>
    shouldItemBeVisible(item, scenarioDataLeft),
  );
  const filteredDataRight = scenarioDataRight.filter((item) =>
    shouldItemBeVisible(item, scenarioDataRight),
  );

  console.log("ScenarioTable after filtering:", {
    filteredDataLeftLength: filteredDataLeft.length,
    filteredDataRightLength: filteredDataRight.length,
    leftKeys: filteredDataLeft.slice(0, 3).map((item) => item.key),
    rightKeys: filteredDataRight.slice(0, 3).map((item) => item.key),
    leftSample: filteredDataLeft[0]?.values,
    rightSample: filteredDataRight[0]?.values,
  });

  // Use the longer dataset for row generation - show empty structure when no data
  const baseData =
    filteredDataLeft.length > 0
      ? filteredDataLeft
      : filteredDataRight.length > 0
        ? filteredDataRight
        : []; // Empty structure - will show just headers

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setFontSize((prev) => Math.min(16, prev + 1)); // Max 16pt
  }, []);

  const handleZoomOut = useCallback(() => {
    setFontSize((prev) => Math.max(8, prev - 1)); // Min 8pt
  }, []);

  // Toggle expand/collapse all
  const handleToggleExpandAll = useCallback(() => {
    // Check if any items are collapsed
    const hasCollapsedItems = baseData.some(
      (row) => row.is_parent && !drillDownsOpen[row.meta_name],
    );

    if (hasCollapsedItems) {
      handleExpandAll();
    } else {
      handleCollapseAll();
    }
  }, [baseData, drillDownsOpen, handleExpandAll, handleCollapseAll]);

  // Mouse event handlers for column resizing
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);
      resizeRef.current = {
        startX: e.clientX,
        startWidth: hierarchyColumnWidth,
      };

      const handleMouseMove = (e) => {
        if (!resizeRef.current) return;

        const deltaX = e.clientX - resizeRef.current.startX;
        const newWidth = Math.max(
          80,
          Math.min(300, resizeRef.current.startWidth + deltaX),
        );
        setHierarchyColumnWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        resizeRef.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [hierarchyColumnWidth],
  );

  // Generate dynamic header structure based on column organization
  // const headerStructure = generateHeaderStructure(
  //   columnOrganization,
  //   visibleNodes,
  //   [selectedScenarios[0] || "AS IS", selectedScenarios[1] || "TO BE"],
  //   nodeStructure,
  // );

  // Helper function to get node metrics count using metadata and visibility
  const getNodeMetricsCount = (nodeKey, scenarioType = null) => {
    const nodeColumns = metadata?.nodes?.[nodeKey]?.columns || [];
    let visibleCount = 0;

    nodeColumns.forEach((column) => {
      if (column.calculation_type === "delta_rate") {
        const deltaKey = `delta_${nodeKey}_${column.column_id}`;
        if (visibleColumns[deltaKey]) {
          // Delta columns only appear in AS IS section for scenario-first
          if (columnOrganization === "scenario-first") {
            if (scenarioType === "asis") {
              visibleCount += 1;
            }
            // Don't count for tobe section
          } else {
            // In elo-first, delta columns appear as single columns
            visibleCount += 1;
          }
        }
      } else {
        const asIsKey = `asis_${nodeKey}_${column.column_id}`;
        const toBeKey = `tobe_${nodeKey}_${column.column_id}`;

        if (columnOrganization === "scenario-first") {
          // In scenario-first, count each visible column separately per scenario
          if (scenarioType === "asis" && visibleColumns[asIsKey]) {
            visibleCount += 1;
          } else if (scenarioType === "tobe" && visibleColumns[toBeKey]) {
            visibleCount += 1;
          }
        } else {
          // In elo-first, count as pairs (both asis and tobe together)
          if (visibleColumns[asIsKey] || visibleColumns[toBeKey]) {
            visibleCount += 2; // Always count as pair for header colspan
          }
        }
      }
    });

    return visibleCount;
  };

  // Helper function to get visible node metrics array using metadata and visibility
  const getNodeMetrics = (nodeKey) => {
    const allColumns = metadata?.nodes?.[nodeKey]?.columns || [];
    return allColumns.filter((column) => {
      if (column.calculation_type === "delta_rate") {
        const deltaKey = `delta_${nodeKey}_${column.column_id}`;
        return visibleColumns[deltaKey];
      } else {
        const asIsKey = `asis_${nodeKey}_${column.column_id}`;
        const toBeKey = `tobe_${nodeKey}_${column.column_id}`;
        return visibleColumns[asIsKey] || visibleColumns[toBeKey];
      }
    });
  };

  // Helper function to render data cells based on column organization
  const renderDataCells = (leftRow, rightRow, hasLeftData, hasRightData) => {
    const visibleNodeKeys = Object.keys(visibleNodes).filter(
      (nodeKey) => visibleNodes[nodeKey],
    );
    const cells = [];

    if (columnOrganization === "scenario-first") {
      // Scenario-first: All AS IS columns, then all TO BE columns
      // AS IS scenario columns
      visibleNodeKeys.forEach((nodeKey) => {
        const nodeMetrics = getNodeMetrics(nodeKey);
        nodeMetrics.forEach((metric) => {
          // Handle delta_rate columns differently - they only have delta values
          if (metric.calculation_type === "delta_rate") {
            const deltaColKey = `delta_${nodeKey}_${metric.column_id}`;
            const cellValue =
              hasLeftData && leftRow.values[deltaColKey] !== undefined
                ? formatCellValue(
                    leftRow.values[deltaColKey],
                    deltaColKey,
                    metadata,
                  )
                : "-";

            cells.push(
              <td
                key={`delta-${nodeKey}-${metric.column_id}`}
                className={classes.valueCell}
                title={cellValue === "-" ? undefined : cellValue}
                style={{}}
              >
                {hasLeftData ? (
                  leftRow.values[deltaColKey] !== undefined ? (
                    formatCellValue(
                      leftRow.values[deltaColKey],
                      deltaColKey,
                      metadata,
                    )
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );
          } else {
            // Regular columns have asis values
            const colKey = `asis_${nodeKey}_${metric.column_id}`;
            const cellValue =
              hasLeftData && leftRow.values[colKey] !== undefined
                ? formatCellValue(leftRow.values[colKey], colKey, metadata)
                : "-";

            cells.push(
              <td
                key={`asis-${nodeKey}-${metric.column_id}`}
                className={classes.valueCell}
                title={cellValue === "-" ? undefined : cellValue}
                style={{}}
              >
                {hasLeftData ? (
                  leftRow.values[colKey] !== undefined ? (
                    formatCellValue(leftRow.values[colKey], colKey, metadata)
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );
          }
        });
      });

      // TO BE scenario columns
      visibleNodeKeys.forEach((nodeKey) => {
        const nodeMetrics = getNodeMetrics(nodeKey);
        nodeMetrics.forEach((metric) => {
          // Skip delta_rate columns for TO BE since they're already handled in AS IS section
          if (metric.calculation_type !== "delta_rate") {
            const colKey = `tobe_${nodeKey}_${metric.column_id}`;

            const cellValue =
              hasRightData && rightRow.values[colKey] !== undefined
                ? formatCellValue(rightRow.values[colKey], colKey, metadata)
                : "-";

            cells.push(
              <td
                key={`tobe-${nodeKey}-${metric.column_id}`}
                className={classes.valueCell}
                title={cellValue === "-" ? undefined : cellValue} // Show full value on hover
              >
                {hasRightData ? (
                  rightRow.values[colKey] !== undefined ? (
                    formatCellValue(rightRow.values[colKey], colKey, metadata)
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );
          }
        });
      });
    } else {
      // Elo-first: For each elo, for each metric, show AS IS then TO BE
      let columnPairIndex = 0; // Track column pairs for alternating colors
      visibleNodeKeys.forEach((nodeKey) => {
        const nodeMetrics = getNodeMetrics(nodeKey);
        nodeMetrics.forEach((metric) => {
          const isAlternateGroup = columnPairIndex % 2 === 1;
          const alternateBackgroundColor = isAlternateGroup
            ? grayColor[13]
            : undefined;

          if (metric.calculation_type === "delta_rate") {
            // Delta columns only have one cell (the delta value)
            const deltaColKey = `delta_${nodeKey}_${metric.column_id}`;
            const deltaCellValue =
              hasLeftData && leftRow.values[deltaColKey] !== undefined
                ? formatCellValue(
                    leftRow.values[deltaColKey],
                    deltaColKey,
                    metadata,
                  )
                : "-";

            cells.push(
              <td
                key={`${nodeKey}-${metric.column_id}-delta`}
                className={classes.valueCell}
                title={deltaCellValue === "-" ? undefined : deltaCellValue}
                style={{
                  backgroundColor: alternateBackgroundColor,
                }}
              >
                {hasLeftData ? (
                  leftRow.values[deltaColKey] !== undefined ? (
                    formatCellValue(
                      leftRow.values[deltaColKey],
                      deltaColKey,
                      metadata,
                    )
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );
          } else {
            // Regular columns have both AS IS and TO BE cells
            // AS IS cell for this metric
            const asisColKey = `asis_${nodeKey}_${metric.column_id}`;
            const asisCellValue =
              hasLeftData && leftRow.values[asisColKey] !== undefined
                ? formatCellValue(
                    leftRow.values[asisColKey],
                    asisColKey,
                    metadata,
                  )
                : "-";

            cells.push(
              <td
                key={`${nodeKey}-${metric.column_id}-asis`}
                className={classes.valueCell}
                title={asisCellValue === "-" ? undefined : asisCellValue} // Show full value on hover
                style={{
                  backgroundColor: alternateBackgroundColor,
                }}
              >
                {hasLeftData ? (
                  leftRow.values[asisColKey] !== undefined ? (
                    formatCellValue(
                      leftRow.values[asisColKey],
                      asisColKey,
                      metadata,
                    )
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );

            // TO BE cell for this metric
            const tobeColKey = `tobe_${nodeKey}_${metric.column_id}`;
            const tobeCellValue =
              hasRightData && rightRow.values[tobeColKey] !== undefined
                ? formatCellValue(
                    rightRow.values[tobeColKey],
                    tobeColKey,
                    metadata,
                  )
                : "-";

            cells.push(
              <td
                key={`${nodeKey}-${metric.column_id}-tobe`}
                className={classes.valueCell}
                title={tobeCellValue === "-" ? undefined : tobeCellValue} // Show full value on hover
                style={{
                  backgroundColor: alternateBackgroundColor,
                }}
              >
                {hasRightData ? (
                  rightRow.values[tobeColKey] !== undefined ? (
                    formatCellValue(
                      rightRow.values[tobeColKey],
                      tobeColKey,
                      metadata,
                    )
                  ) : (
                    "-"
                  )
                ) : (
                  <span className={classes.placeholderText}>-</span>
                )}
              </td>,
            );
          }

          columnPairIndex++; // Increment after each metric
        });
      });
    }

    return cells;
  };

  // Helper function to render headers dynamically
  const renderDynamicHeaders = () => {
    if (columnOrganization === "scenario-first") {
      return renderScenarioFirstHeaders();
    } else {
      return renderEloFirstHeaders();
    }
  };

  // Render headers for scenario-first organization
  const renderScenarioFirstHeaders = () => {
    const visibleNodeKeys = Object.keys(visibleNodes).filter(
      (nodeKey) => visibleNodes[nodeKey],
    );
    const asIsColSpan = visibleNodeKeys.reduce(
      (sum, nodeKey) => sum + getNodeMetricsCount(nodeKey, "asis"),
      0,
    );
    const toBeColSpan = visibleNodeKeys.reduce(
      (sum, nodeKey) => sum + getNodeMetricsCount(nodeKey, "tobe"),
      0,
    );

    return (
      <>
        {/* Scenario headers row */}
        <tr>
          {/* Dimensions column header with expand/collapse controls */}
          <th
            className={`${classes.headerCell} ${classes.hierarchyHeaderCell}`}
            rowSpan="3"
            style={{
              width: `${hierarchyColumnWidth}px`,
              minWidth: `${hierarchyColumnWidth}px`,
              maxWidth: `${hierarchyColumnWidth}px`,
              position: "sticky",
              left: 0,
              top: 0,
              zIndex: 5,
            }}
          >
            <div className={classes.expandCollapseContainer}>
              {/* Single fold/unfold button */}
              <div
                className={classes.expandCollapseButton}
                onClick={handleToggleExpandAll}
              >
                <FontAwesomeIcon
                  icon={
                    baseData.some(
                      (row) => row.is_parent && !drillDownsOpen[row.meta_name],
                    )
                      ? faAngleDoubleDown
                      : faAngleDoubleUp
                  }
                />
                <span style={{ marginLeft: "4px", fontSize: "9px" }}>
                  {baseData.some(
                    (row) => row.is_parent && !drillDownsOpen[row.meta_name],
                  )
                    ? "Unfold All"
                    : "Fold All"}
                </span>
              </div>

              {/* Zoom controls */}
              <div className={classes.zoomButtonsContainer}>
                <div
                  className={classes.zoomButton}
                  onClick={handleZoomIn}
                  style={{ opacity: fontSize >= 16 ? 0.5 : 1 }}
                >
                  <FontAwesomeIcon icon={faSearchPlus} />
                </div>
                <div
                  className={classes.zoomButton}
                  onClick={handleZoomOut}
                  style={{ opacity: fontSize <= 8 ? 0.5 : 1 }}
                >
                  <FontAwesomeIcon icon={faSearchMinus} />
                </div>
              </div>
            </div>
            {/* Resize handle */}
            <div
              className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ""}`}
              onMouseDown={handleMouseDown}
            />
          </th>
          {/* AS IS scenario header */}
          <th className={classes.scenarioHeader} colSpan={asIsColSpan}>
            {selectedScenarios[0]?.scenario_name ||
              selectedScenarios[0] ||
              "AS IS"}
          </th>
          {/* TO BE scenario header */}
          <th className={classes.scenarioHeader} colSpan={toBeColSpan}>
            {selectedScenarios[1]?.scenario_name ||
              selectedScenarios[1] ||
              "TO BE"}
          </th>
        </tr>
        {/* Node header row for scenario-first */}
        <tr className={classes.nodeHeaderRow}>
          {/* AS IS scenario node headers */}
          {visibleNodeKeys.map((nodeKey, index) => {
            const nodeStructureData = nodeStructure[nodeKey];
            const nodeMetricsCount = getNodeMetricsCount(nodeKey, "asis");

            return (
              <th
                key={`asis-${nodeKey}`}
                className={`${classes.nodeHeaderCell} ${classes[`eloHeader${index}`]}`}
                colSpan={nodeMetricsCount}
                title={nodeStructureData.name}
                style={{}}
              >
                {nodeStructureData.name}
              </th>
            );
          })}
          {/* TO BE scenario node headers */}
          {visibleNodeKeys.map((nodeKey, index) => {
            const nodeStructureData = nodeStructure[nodeKey];
            const nodeMetricsCount = getNodeMetricsCount(nodeKey, "tobe");

            return (
              <th
                key={`tobe-${nodeKey}`}
                className={`${classes.nodeHeaderCell} ${classes[`eloHeader${index}`]}`}
                colSpan={nodeMetricsCount}
                title={nodeStructureData.name}
              >
                {nodeStructureData.name}
              </th>
            );
          })}
        </tr>
        {/* Column header row for scenario-first */}
        <tr className={classes.headerRow}>
          {/* AS IS scenario column headers */}
          {visibleNodeKeys.map((nodeKey, nodeIndex) => {
            const nodeMetrics = getNodeMetrics(nodeKey);
            return nodeMetrics.map((metric) => {
              // Handle delta_rate columns differently - they only appear in AS IS section
              if (metric.calculation_type === "delta_rate") {
                return (
                  <th
                    key={`delta-${nodeKey}-${metric.column_id}`}
                    className={`${classes.nodeHeaderCell} ${classes[`eloHeader${nodeIndex}`]}`}
                    title={`${metric.column_name}Δ`}
                    style={{
                      minHeight: "20px",
                      lineHeight: "1.1",
                      fontSize: `${fontSize}px`,
                      overflow: "visible",
                      whiteSpace: "normal",
                      verticalAlign: "top",
                    }}
                  >
                    {metric.column_name}Δ
                  </th>
                );
              } else {
                // Regular columns appear in AS IS section
                return (
                  <th
                    key={`asis-${nodeKey}-${metric.column_id}`}
                    className={`${classes.nodeHeaderCell} ${classes[`eloHeader${nodeIndex}`]}`}
                    title={metric.column_name}
                    style={{
                      minHeight: "20px",
                      lineHeight: "1.1",
                      fontSize: `${fontSize}px`,
                      overflow: "visible",
                      whiteSpace: "normal",
                      verticalAlign: "top",
                    }}
                  >
                    {metric.column_name}
                  </th>
                );
              }
            });
          })}
          {/* TO BE scenario column headers */}
          {visibleNodeKeys.map((nodeKey, nodeIndex) => {
            const nodeMetrics = getNodeMetrics(nodeKey);
            return nodeMetrics
              .map((metric) => {
                // Skip delta_rate columns for TO BE since they're already handled in AS IS section
                if (metric.calculation_type !== "delta_rate") {
                  return (
                    <th
                      key={`tobe-${nodeKey}-${metric.column_id}`}
                      className={`${classes.nodeHeaderCell} ${classes[`eloHeader${nodeIndex}`]}`}
                      title={metric.column_name}
                      style={{
                        minHeight: "20px",
                        lineHeight: "1.1",
                        fontSize: `${fontSize}px`,
                        overflow: "visible",
                        whiteSpace: "normal",
                        verticalAlign: "top",
                      }}
                    >
                      {metric.column_name}
                    </th>
                  );
                }
                return null; // Don't render delta columns in TO BE section
              })
              .filter(Boolean); // Remove null entries
          })}
        </tr>
      </>
    );
  };

  // Render headers for elo-first organization
  const renderEloFirstHeaders = () => {
    const visibleNodeKeys = Object.keys(visibleNodes).filter(
      (nodeKey) => visibleNodes[nodeKey],
    );

    return (
      <>
        {/* Elo headers row */}
        <tr>
          {/* Dimensions column header with expand/collapse controls */}
          <th
            className={`${classes.headerCell} ${classes.hierarchyHeaderCell}`}
            rowSpan="3"
            style={{
              width: `${hierarchyColumnWidth}px`,
              minWidth: `${hierarchyColumnWidth}px`,
              maxWidth: `${hierarchyColumnWidth}px`,
              position: "sticky",
              left: 0,
              top: 0,
              zIndex: 5,
            }}
          >
            <div className={classes.expandCollapseContainer}>
              {/* Single fold/unfold button */}
              <div
                className={classes.expandCollapseButton}
                onClick={handleToggleExpandAll}
              >
                <FontAwesomeIcon
                  icon={
                    baseData.some(
                      (row) => row.is_parent && !drillDownsOpen[row.meta_name],
                    )
                      ? faAngleDoubleDown
                      : faAngleDoubleUp
                  }
                />
                <span style={{ marginLeft: "4px", fontSize: "9px" }}>
                  {baseData.some(
                    (row) => row.is_parent && !drillDownsOpen[row.meta_name],
                  )
                    ? "Unfold All"
                    : "Fold All"}
                </span>
              </div>

              {/* Zoom controls */}
              <div className={classes.zoomButtonsContainer}>
                <div
                  className={classes.zoomButton}
                  onClick={handleZoomIn}
                  style={{ opacity: fontSize >= 16 ? 0.5 : 1 }}
                >
                  <FontAwesomeIcon icon={faSearchPlus} />
                </div>
                <div
                  className={classes.zoomButton}
                  onClick={handleZoomOut}
                  style={{ opacity: fontSize <= 8 ? 0.5 : 1 }}
                >
                  <FontAwesomeIcon icon={faSearchMinus} />
                </div>
              </div>
            </div>
            {/* Resize handle */}
            <div
              className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ""}`}
              onMouseDown={handleMouseDown}
            />
          </th>
          {/* Elo headers */}
          {visibleNodeKeys.map((nodeKey, index) => {
            const nodeStructureData = nodeStructure[nodeKey];
            const nodeMetricsCount = getNodeMetricsCount(nodeKey);

            return (
              <th
                key={`elo-${nodeKey}`}
                className={`${classes.nodeHeaderCell} ${classes[`eloHeader${index}`]}`}
                colSpan={nodeMetricsCount}
                title={nodeStructureData.name}
                style={{
                  // Override sticky position for elo-first: ELO headers are at top
                  top: 0,
                  zIndex: 4,
                  height: "26px", // Match scenario-first scenario height for 70px total
                  lineHeight: "26px",
                }}
              >
                {nodeStructureData.name}
              </th>
            );
          })}
        </tr>
        {/* Column metrics headers row for elo-first (moved to middle) */}
        <tr
          className={classes.nodeHeaderRow}
          style={{ top: "26px", zIndex: 3 }}
        >
          {visibleNodeKeys
            .map((nodeKey, nodeIndex) => {
              const nodeMetrics = getNodeMetrics(nodeKey);

              return nodeMetrics
                .map((metric) => {
                  const colSpan =
                    metric.calculation_type === "delta_rate" ? 1 : 2; // Delta columns span 1, regular columns span 2
                  return (
                    <th
                      key={`${nodeKey}-${metric.column_id}-metric-header`}
                      className={`${classes.nodeHeaderCell} ${classes[`eloHeader${nodeIndex}`]}`}
                      colSpan={colSpan}
                      title={metric.column_name}
                      style={{
                        // Override sticky position for elo-first: metric headers are middle
                        top: "26px",
                        zIndex: 3,
                        height: "24px", // Match updated node header height
                        lineHeight: "24px",
                      }}
                    >
                      {metric.column_name}
                    </th>
                  );
                })
                .flat();
            })
            .flat()}
        </tr>
        {/* Scenario headers row for elo-first (moved to bottom) */}
        <tr
          className={classes.headerRow}
          style={{ position: "sticky", top: "50px", zIndex: 2 }}
        >
          {visibleNodeKeys
            .map((nodeKey) => {
              const nodeMetrics = getNodeMetrics(nodeKey);

              return nodeMetrics
                .map((metric) => {
                  if (metric.calculation_type === "delta_rate") {
                    // Delta columns only have one scenario header (Delta)
                    return [
                      <th
                        key={`${nodeKey}-${metric.column_id}-delta`}
                        className={classes.scenarioHeader}
                        title={`${metric.column_name} (Δ%)`}
                        style={{
                          // Ensure sticky position for elo-first: scenario headers are bottom
                          position: "sticky",
                          top: "50px",
                          zIndex: 2,
                          height: "20px", // Thinnest height for bottom row
                          lineHeight: "20px",
                        }}
                      >
                        Δ
                      </th>,
                    ];
                  } else {
                    // Regular columns have both AS IS and TO BE
                    return [
                      <th
                        key={`${nodeKey}-${metric.column_id}-asis`}
                        className={classes.scenarioHeader}
                        title={`${metric.column_name} (AS IS)`}
                        style={{
                          // Ensure sticky position for elo-first: scenario headers are bottom
                          position: "sticky",
                          top: "50px",
                          zIndex: 2,
                          height: "20px", // Thinnest height for bottom row
                          lineHeight: "20px",
                        }}
                      >
                        {selectedScenarios[0]?.scenario_name ||
                          selectedScenarios[0] ||
                          "AS IS"}
                      </th>,
                      <th
                        key={`${nodeKey}-${metric.column_id}-tobe`}
                        className={classes.scenarioHeader}
                        title={`${metric.column_name} (TO BE)`}
                        style={{
                          // Ensure sticky position for elo-first: scenario headers are bottom
                          position: "sticky",
                          top: "50px",
                          zIndex: 2,
                          height: "20px", // Thinnest height for bottom row
                          lineHeight: "20px",
                        }}
                      >
                        {selectedScenarios[1]?.scenario_name ||
                          selectedScenarios[1] ||
                          "TO BE"}
                      </th>,
                    ];
                  }
                })
                .flat();
            })
            .flat()}
        </tr>
      </>
    );
  };

  return (
    <div className={classes.scenarioColumn}>
      <div className={classes.scenarioContent}>
        {/* Unified table with both scenarios */}
        <table className={classes.pnlTable}>
          <thead>{renderDynamicHeaders()}</thead>
          <tbody>
            {baseData.map((row) => {
              // Find corresponding row in each scenario or use placeholder
              const leftRow =
                filteredDataLeft.find((r) => r.key === row.key) || row;
              const rightRow =
                filteredDataRight.find((r) => r.key === row.key) || row;
              const hasLeftData =
                selectedScenarios[0] &&
                selectedScenarios[0] !== null &&
                filteredDataLeft.find((r) => r.key === row.key);
              const hasRightData =
                selectedScenarios[1] &&
                selectedScenarios[1] !== null &&
                filteredDataRight.find((r) => r.key === row.key);

              return (
                <tr key={row.key} className={classes.dataRow}>
                  {/* Dimensions cell */}
                  <td
                    className={classes.hierarchyCell}
                    onClick={() => {
                      // Only handle click for parent items
                      if (row.is_parent) {
                        console.log(
                          "Hierarchy cell clicked for:",
                          row.meta_name,
                        );
                        handleDrillDownClick(row.meta_name);
                      }
                    }}
                    style={{
                      cursor: row.is_parent ? "pointer" : "default",
                      width: `${hierarchyColumnWidth}px`,
                      minWidth: `${hierarchyColumnWidth}px`,
                      maxWidth: `${hierarchyColumnWidth}px`,
                      position: "relative",
                    }}
                  >
                    <div style={{ marginLeft: `${(row.level - 1) * 8}px` }}>
                      {row.is_parent && (
                        <span className={classes.expandIcon}>
                          {drillDownsOpen[row.meta_name] ? "▼" : "►"}
                        </span>
                      )}
                      {!row.is_parent && (
                        <span
                          style={{ width: "10px", display: "inline-block" }}
                        />
                      )}
                      <span title={row.name}>{row.name}</span>
                    </div>
                    {/* Resize handle for each hierarchy cell */}
                    <div
                      className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ""}`}
                      onMouseDown={handleMouseDown}
                    />
                  </td>
                  {/* Data cells organized by column organization */}
                  {renderDataCells(
                    leftRow,
                    rightRow,
                    hasLeftData,
                    hasRightData,
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ScenarioTable.propTypes = {
  selectedScenarios: PropTypes.array.isRequired,
  hierarchyOrder: PropTypes.array.isRequired,
  visibleNodes: PropTypes.object.isRequired,
  visibleColumns: PropTypes.object.isRequired,
  columnOrganization: PropTypes.oneOf(["scenario-first", "elo-first"])
    .isRequired,
  nodeStructure: PropTypes.object.isRequired,
  metadata: PropTypes.object,
  getScenarioData: PropTypes.func.isRequired,
  shouldItemBeVisible: PropTypes.func.isRequired,
  handleDrillDownClick: PropTypes.func.isRequired,
  drillDownsOpen: PropTypes.object.isRequired,
  handleExpandAll: PropTypes.func.isRequired,
  handleCollapseAll: PropTypes.func.isRequired,
  formatNumber: PropTypes.func.isRequired,
};

export default ScenarioTable;
